import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormGroup, FormsModule } from '@angular/forms';
import {
  KebabMenuItem,
  QaiBreadcrumbListComponent,
  QaiButtonComponent,
  QaiKebabMenuComponent,
  QaiTabDirective,
  QaiTabsComponent,
} from '@qai/ui';
import { GeneralContentComponent } from './general-details/general-content.component';
import { TestingDetailsComponent } from './testing-details/testing-details.component';
import { ContentDetailComponent } from './content-details/content-detail.component';
import { ScoringDetailsComponent } from './scoring-details/scoring-details.component';
import { KeyWordsDetailsComponent } from './key-words-details/key-words-details.component';
import { PublishSimulationModalComponent } from '../components/modals/publish-simulation/publish-simulation-modal.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { CommonModule, DatePipe } from '@angular/common';
import { of } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';

import { AuthorReactiveFormService } from '../services/author-reactive-form.service';
import { IFetchSimulationDetails } from 'src/app/interfaces/self-serve/responses/ifetch-simulation-details';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { DeleteModalComponent } from '../components/modals/delete-modal/delete-modal.component';
import { ActionModalComponent } from '../components/modals/simulation-assignment-action-modal/action-modal.component';
import { CallTypeLabelMap, SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import {
  EntityActionType,
  EntityType,
} from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { GuidedSimulationDetailsComponent } from './guided-simulation-details/guided-simulation-details.component';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/author.service';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';

@Component({
  selector: 'simulation',
  templateUrl: './simulation-details.component.html',
  styleUrls: ['./simulation-details.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    QaiButtonComponent,
    QaiBreadcrumbListComponent,
    DeleteModalComponent,
    ActionModalComponent,
    QaiTabsComponent,
    QaiTabDirective,
    GeneralContentComponent,
    TestingDetailsComponent,
    ContentDetailComponent,
    ScoringDetailsComponent,
    PublishSimulationModalComponent,
    QaiKebabMenuComponent,
    DatePipe,
    CommonModule,
    GuidedSimulationDetailsComponent,
    KeyWordsDetailsComponent,
  ],
})
export class SimulationDetailsComponent {
  simulationName: string = '';
  description: string = '';
  openPublishModal = false;
  duplicateModalVisible = false;
  networkRequestInProgress = false;
  simulationToDuplicateName: string = '';
  modalErrorStatus?: number;

  options: KebabMenuItem[] = [];

  form!: FormGroup;
  lastSaved: string | null = null;
  callUuid: string = '';
  loading: boolean = false;
  readOnly: boolean = false;
  readyForPublish: boolean = false;
  isDeleteModalOpen = false;
  callType: string | null = null;
  showKeyWordsTab: boolean = false;

  simulationDetails: IFetchSimulationDetails | null = null;

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private featureFlagsService: FeatureFlagsService
  ) {}

  EntityActionType = EntityActionType;
  EntityType = EntityType;

  async ngOnInit(): Promise<void> {
    this.showKeyWordsTab = await this.featureFlagsService.isFlagEnabled(
      AppConstants.KEY_WORDS
    );
    console.log('Key_Words feature flag:', this.showKeyWordsTab);

    this.route.paramMap?.subscribe((params) => {
      const navigation = this.router.getCurrentNavigation();
      const routeState = navigation?.extras?.state ?? history.state;

      this.callType = routeState?.callType || null;
      const moduleUuid = this.route.snapshot.queryParamMap.get('moduleUuid') || '';

      const uuid = params.get('uuid');
      if (!uuid) return;

      this.callUuid = uuid;
      this.loading = true;

      this.authorService
        .getSimulationDetails(this.callUuid, moduleUuid)
        .pipe(
          switchMap((simulationDetails) => {
            if (!simulationDetails) {
              console.error('Failed to fetch simulation details');
              this.loading = false;
              return of({ simulationDetails: null, keywords: undefined });
            }

            const personaUuid = simulationDetails.callPersona?.uuid || '';

            // Fetch keywords with personaUuid
            return this.authorService
              .getWordDetails(this.callUuid, personaUuid)
              .pipe(
                catchError((error) => {
                  console.warn(
                    'Failed to fetch keywords, continuing with empty keywords:',
                    error
                  );
                  return of(undefined);
                }),
                map((keywords) => ({ simulationDetails, keywords }))
              );
          })
        )
        .subscribe({
          next: ({ simulationDetails, keywords }) => {
            if (!simulationDetails) {
              return;
            }

          this.simulationDetails = simulationDetails;
          
          // Initialize form with both simulation details and keywords
          this.form = this.authorReactiveFormService.initializeSimulationForm(
            this.callUuid,
            simulationDetails,
            keywords  // Pass keywords data here
          );

          this.simulationName =
            this.form.get('general.simulationName')?.value || '';

          this.form
            .get('general.simulationName')
            ?.valueChanges?.subscribe((value) => {
              this.simulationName = value;
            });

          this.readOnly = this.authorReactiveFormService.isReadOnly(
            this.callUuid
          );

          this.form?.statusChanges?.subscribe(() => {
            this.readOnly = this.authorReactiveFormService.isReadOnly(
              this.callUuid
            );
            this.readyForPublish = this.form.valid;
          });

          const status = simulationDetails.status?.toLowerCase();

          if (this.callType === CallTypeLabelMap.guided) {
            this.options = [];
          } else {
            this.options = [
              { type: 'click', text: 'Duplicate' } as KebabMenuItem,
              ...(status !== 'published'
                ? [{ type: 'click', text: 'Delete' } as KebabMenuItem]
                : []),
            ];
          }

          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching simulation data:', error);
          this.loading = false;
        }
      });

      this.authorReactiveFormService
        .getUpdatedAt$(uuid)
        ?.subscribe((timestamp) => {
          this.lastSaved = timestamp;
        });
    });
  }

  ngOnDestroy() {
    this.authorReactiveFormService.deleteSimulation(this.callUuid);
  }

  publishSimulation(): void {
    this.openPublishModal = true;
  }

  handlePublishModalConfirm() {
    this.openPublishModal = false;
  }

  handlePublishModalClose() {
    this.openPublishModal = false;
  }

  onKebabMenuAction(action: any) {
    switch (action.item.text.toLowerCase()) {
      case 'duplicate':
        this.simulationToDuplicateName = this.simulationName;
        this.duplicateModalVisible = true;
        break;
      case 'delete':
        this.openDeleteModal();
        break;
    }
  }

  onSimulationDuplicated(data: IConfirmActionPayload) {
    this.networkRequestInProgress = true;
    this.modalErrorStatus = undefined;

    const payload = {
      callUuid: this.callUuid,
      callTitle: data.name,
      moduleUuid: data.moduleUuid || this.form?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '',
    };

    this.authorService.duplicateSimulationAPI(payload).subscribe({
      next: (newSimulation) => {
        this.closeDuplicateModal();
        const moduleUuid = payload.moduleUuid;
        this.router.navigate(['/self-serve/simulations', newSimulation.uuid], {
          queryParams: { moduleUuid, fromSimulationDetails: 'true' },
        });
      },
      error: (err) => {
        console.error('Error duplicating simulation:', err);
        this.networkRequestInProgress = false;

        if (err?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
          this.modalErrorStatus = err.status;
        }
      },
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  closeDuplicateModal() {
    this.duplicateModalVisible = false;
    this.simulationToDuplicateName = '';
    this.modalErrorStatus = undefined;
    this.cdRef.detectChanges();
  }

  openDeleteModal() {
    this.isDeleteModalOpen = true;
  }

  confirmDelete() {
    this.networkRequestInProgress = true;
    const moduleUuid = this.form?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    this.authorService.deleteSimulationAPI(this.callUuid, moduleUuid).subscribe({
      next: () => {
        this.isDeleteModalOpen = false;
        this.router.navigate(['/self-serve'], {
          queryParams: { fromSimulationDetails: 'true' },
        });
        this.networkRequestInProgress = false;
      },
      error: (err) => {
        console.error('Error deleting simulation:', err);
        this.networkRequestInProgress = false;
      },
    });
  }
}
